/**
 * Prompt-library helpers for the AI side panel (SellerMate W6.3): search,
 * grouping by category, and the plain-English label of a schedule.
 * Search uses the app's one grammar (./searchQuery.ts) over the title,
 * prompt text and category name.
 */
import { matchesQuery } from './searchQuery'
import type { Cadence, LibraryPrompt, PromptCategory } from './aiApi'

export const ALL_CATEGORIES = 'all'

export function filterPrompts(
  prompts: readonly LibraryPrompt[],
  categories: readonly PromptCategory[],
  query: string,
  category: string = ALL_CATEGORIES,
): LibraryPrompt[] {
  const labelOf = new Map(categories.map((c) => [c.key, c.label]))
  return prompts.filter((p) =>
    (category === ALL_CATEGORIES || p.category === category)
    && matchesQuery(query, [p.title, p.prompt, labelOf.get(p.category) ?? p.category]))
}

export type PromptGroup = { category: PromptCategory; prompts: LibraryPrompt[] }

/** Prompts grouped in the library's category order; empty groups dropped. */
export function groupPrompts(
  prompts: readonly LibraryPrompt[],
  categories: readonly PromptCategory[],
): PromptGroup[] {
  return categories
    .map((category) => ({ category, prompts: prompts.filter((p) => p.category === category.key) }))
    .filter((g) => g.prompts.length > 0)
}

export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'] as const

export function hourLabel(hour: number): string {
  const h = ((hour % 24) + 24) % 24
  const suffix = h < 12 ? 'AM' : 'PM'
  const h12 = h % 12 === 0 ? 12 : h % 12
  return `${h12} ${suffix}`
}

/** "Every day at 7 AM ET" / "Mondays at 7 AM ET". */
export function scheduleLabel(s: { cadence: Cadence; weekday: number | null; hour_et: number }): string {
  const at = `at ${hourLabel(s.hour_et)} ET`
  if (s.cadence === 'weekly') {
    const day = s.weekday != null ? WEEKDAYS[s.weekday] : undefined
    return day ? `${day}s ${at}` : `Weekly ${at}`
  }
  return `Every day ${at}`
}
